/**
 * Bestanden delen — /bestanden (okt 2026).
 *
 * Joris: "Bestanden die Claude (of ik) maak intern naar een collega sturen via Toolframe."
 * Uploaden (pdf, docx, xlsx, pptx, png/jpg/gif, mov/mp4, zip, txt/md/csv, max. 25 GB), collega's
 * kiezen (@fullframe.nl), korte notitie. De ontvanger ziet het onder "Voor mij" (bel + mail met
 * link naar /bestanden?id=…), de afzender onder "Verstuurd". Bestanden blijven 90 dagen staan;
 * "Bewaren" geeft er 90 dagen bij.
 *
 * Mappen (okt 2026, "kan je het zo maken dat je ook folders kan delen"): kies of sleep ook een
 * hele map (of meerdere bestanden). Het wordt één pakket met één notitie en één mail; verborgen
 * bestanden en niet-toegestane typen worden overgeslagen en getoond. De ontvanger ziet de map als
 * uitklapbare boom en downloadt per bestand of "Alles als zip" (de server stroomt de zip direct
 * uit de opslag).
 *
 * Grote bestanden (okt 2026, "bestand van bijna 10 GB versturen"): 25 GB per bestand, 50 GB per
 * pakket. De upload herhaalt haperende blokken (5 pogingen, oplopend wachten), stuurt 3 blokken
 * tegelijk, toont % en de geschatte resterende tijd, waarschuwt bij het sluiten van het tabblad en
 * gaat na een mislukte poging bij opnieuw Versturen verder waar hij was. "Alles als zip" kan tot
 * 10 GB (elk bestand onder 4 GB); een grotere map download je per bestand.
 *
 * Vangnetten: versturen = terughaalknop (5 s Annuleren, utils/uitgesteld) want er gaat een
 * mail uit; verwijderen = bevestiging (utils/bevestig, gevaarlijk).
 *
 * Tijdstippen (okt 2026, "tijdstippen van doorsturen en downloaden"): Verstuurd toont het
 * verzendmoment en per ontvanger geopend/gedownload met tijd (meerdere downloads: aantal + laatste);
 * Voor mij toont wanneer je hem ontving en, als je hem downloadde, wanneer. NL-tijd, vandaag/gisteren
 * relatief, voluit in de tooltip. De server geeft de tijden van andere ontvangers alleen aan de afzender.
 *
 * Externe ontvangers (okt 2026, "kan je met bestanden doorsturen ook doorsturen naar externen"): naast
 * de collega-kiezer een vrij veld voor externe e-mailadressen (max. 10, gemarkeerd "extern"), met een
 * geldigheid van 1–90 dagen (standaard 14). Elk extern adres krijgt een mail met een persoonlijke
 * downloadlink (/download/<token>, zonder inloggen). Vangnet: eerst een bevestiging "Je stuurt dit naar
 * een extern adres: …", dan de 5 s Annuleren. Verstuurd toont per extern adres gedownload ja/nee en
 * "Intrekken" (met Ongedaan maken). Op test gaat er geen mail uit: dan toont het scherm wat er
 * verstuurd zou zijn, met de link om zelf te openen.
 *
 * Rustig: één accentkleur, geen kaarten of pillen, secundaire info klein en grijs.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  LinearProgress, Stack, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import toast from 'react-hot-toast';
import PageHeader from '../../ui-components/PageHeader';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { voerUitgesteldUit } from '../../utils/uitgesteld';
import { meldFout } from '../../utils/apiFout';
import {
  ACCEPT, GRENS_TEKST, MAX_DAGEN, MAX_EXTERN, STANDAARD_DAGEN, aantalTekst, api, bewaar, bouwBoom, controleerExtern, downloadTekst,
  downloadTitel, externDelen, filterBestanden, geldigeDagen, grootteTekst, haalCollegas, haalDetail, haalLijst,
  herstelLink, ontvangerDelen, startDownload, telBoom, tijdTekst, trekLinkIn, uitDrop, uitInvoer, uploadPakket,
  verlooptTekst, verstuur, volledigeTijd, voornaam, vraagDownload, vraagItemDownload, vraagZip, zipUrl,
  nieuweUploadStaat, restTekst, zipKan, ZIP_TE_GROOT,
} from './bestandenApi';

const KLEUR = {
  tekst: '#1f2937', zacht: '#6b7280', lijn: '#eceef1', accent: '#526075', accentZacht: '#eef2f8',
};
const KNOP = { textTransform: 'none', fontWeight: 600 };

const META = { fontSize: 13, color: KLEUR.zacht };

/** Eén stukje metaregel, met de volledige datum en tijd als tooltip. */
function Tijd({ tekst, titel }) {
  if (!tekst) return null;
  return <span title={titel || undefined}>{tekst}</span>;
}

/** Losse delen achter elkaar met " · " ertussen (lege delen vallen weg). */
function Regel({ delen, testId }) {
  const zichtbaar = delen.filter((d) => d && d.tekst);
  return (
    <Typography data-testid={testId} sx={{ ...META, mt: 0.25 }}>
      {zichtbaar.map((d, i) => (
        <React.Fragment key={`${i}-${d.tekst}`}>{i > 0 && ' · '}<Tijd {...d} /></React.Fragment>
      ))}
    </Typography>
  );
}

const ZICHTBAAR = 5;

/** Verstuurd: per ontvanger een regel "Michel · geopend 8 okt 14:40 · gedownload 8 okt 15:02". */
function Ontvangers({ ontvangers }) {
  const [alles, setAlles] = useState(false);
  const lijst = ontvangers || [];
  const zichtbaar = alles ? lijst : lijst.slice(0, ZICHTBAAR);
  return (
    <Box data-testid="ontvangers">
      {zichtbaar.map((o) => (
        <Regel key={o.email} testId={`ontvanger-${o.email}`}
          delen={[{ tekst: voornaam(o), titel: o.email }, ...ontvangerDelen(o)]} />
      ))}
      {lijst.length > ZICHTBAAR && (
        <Button size="small" onClick={(e) => { e.stopPropagation(); setAlles((x) => !x); }}
          sx={{ ...KNOP, ...META, p: 0, minWidth: 0 }}>
          {alles ? 'Minder' : `En nog ${lijst.length - ZICHTBAAR}`}
        </Button>
      )}
    </Box>
  );
}

/** Kleine grijze markering "extern" (zelfde rustige stijl als de metaregel). */
function ExternLabel() {
  return (
    <Box component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
      color: KLEUR.accent, border: `1px solid ${KLEUR.accent}`, borderRadius: 0.75, px: 0.5, py: '1px', lineHeight: 1.4 }}>
      extern
    </Box>
  );
}

/** Verstuurd: per extern adres "klant@x.nl · EXTERN · gedownload 8 okt 15:02 · geldig t/m 3 nov · Intrekken". */
function Externen({ links, onIntrek, bezig }) {
  if (!links?.length) return null;
  return (
    <Box data-testid="externen">
      {links.map((x) => (
        <Box key={x.id} data-testid={`extern-${x.email}`} sx={{ ...META, mt: 0.25, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <span>{x.email}</span>
          <ExternLabel />
          {externDelen(x).map((d, i) => (
            <React.Fragment key={`${i}-${d.tekst}`}><span>·</span><Tijd {...d} /></React.Fragment>
          ))}
          {x.status === 'actief' && (
            <Button size="small" disabled={bezig} onClick={(e) => { e.stopPropagation(); onIntrek(x); }}
              sx={{ ...KNOP, ...META, p: 0, minWidth: 0, color: KLEUR.accent }}>
              Intrekken
            </Button>
          )}
        </Box>
      ))}
    </Box>
  );
}

// ── boom ──────────────────────────────────────────────────────────────────
function MapKnoop({ map, diepte, onDownload, bezig }) {
  const [open, setOpen] = useState(diepte === 0);
  const t = telBoom(map.knoop);
  return (
    <Box>
      <Box component="button" type="button" onClick={() => setOpen((x) => !x)} aria-expanded={open}
        sx={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5,
          pl: diepte * 2, color: KLEUR.tekst, fontSize: 14, boxSizing: 'border-box', width: '100%' }}>
        <Box component="span" sx={{ width: 12, color: KLEUR.zacht, fontSize: 11 }}>{open ? '▾' : '▸'}</Box>
        <FolderOutlinedIcon sx={{ fontSize: 16, color: KLEUR.zacht }} />
        <span>{map.naam}</span>
        <Box component="span" sx={{ color: KLEUR.zacht, fontSize: 12 }}>{aantalTekst(t.aantal)} · {grootteTekst(t.grootte)}</Box>
      </Box>
      {open && <BoomInhoud knoop={map.knoop} diepte={diepte + 1} onDownload={onDownload} bezig={bezig} />}
    </Box>
  );
}

function BoomInhoud({ knoop, diepte, onDownload, bezig }) {
  return (
    <>
      {knoop.mappen.map((m) => <MapKnoop key={m.pad} map={m} diepte={diepte} onDownload={onDownload} bezig={bezig} />)}
      {knoop.bestanden.map((b) => (
        <Box key={b.id} data-testid={`boom-bestand-${b.id}`}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25, pl: diepte * 2 + 2.25, fontSize: 14 }}>
          <Box component="span" sx={{ color: KLEUR.tekst, wordBreak: 'break-word', flex: 1, minWidth: 0 }}>{b.naam}</Box>
          <Box component="span" sx={{ color: KLEUR.zacht, fontSize: 12, flexShrink: 0 }}>{grootteTekst(b.grootte)}</Box>
          <Button size="small" disabled={bezig} onClick={() => onDownload(b)} aria-label={`Download ${b.naam}`}
            sx={{ ...KNOP, color: KLEUR.accent, minWidth: 0, py: 0, flexShrink: 0 }}>
            Download
          </Button>
        </Box>
      ))}
    </>
  );
}

/** De inhoud van een gedeelde map als uitklapbare boom (bovenste niveau open). */
export function Boom({ items, onDownload, bezig }) {
  return (
    <Box data-testid="boom" sx={{ mt: 1.25, pt: 1, borderTop: `1px dashed ${KLEUR.lijn}` }}>
      <BoomInhoud knoop={bouwBoom(items)} diepte={0} onDownload={onDownload} bezig={bezig} />
    </Box>
  );
}

// ── rij in de lijst ───────────────────────────────────────────────────────
function Rij({ item, soort, gemarkeerd, onOpen, onDownload, onZip, onItemDownload, onBewaar, onVerwijder, onIntrek, bezig }) {
  const ref = useRef(null);
  const [inhoud, setInhoud] = useState(null);   // null = dicht; anders de items van het pakket
  const [laden, setLaden] = useState(false);
  const isMap = Boolean(item.is_map);

  const wisselInhoud = useCallback(async () => {
    if (inhoud) { setInhoud(null); return; }
    setLaden(true);
    try {
      const d = await haalDetail(item.id);
      setInhoud(d.items || []);
    } catch (e) { meldFout(e, 'Inhoud laden'); } finally { setLaden(false); }
  }, [inhoud, item.id]);

  useEffect(() => {
    if (gemarkeerd && ref.current?.scrollIntoView) ref.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    if (gemarkeerd && isMap && !inhoud) wisselInhoud();   // link uit mail/bel: de map meteen open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gemarkeerd]);

  const ongelezen = soort === 'voor_mij' && !item.gelezen;
  const verzonden = { titel: volledigeTijd(item.verzonden_at) };
  const verloopt = { tekst: verlooptTekst(item.verloopt_over_dagen) };
  const meta = soort === 'voor_mij'
    ? [
      { ...verzonden, tekst: ['Ontvangen', tijdTekst(item.verzonden_at), 'van', voornaam({ naam: item.afzender_naam, email: item.afzender })].filter(Boolean).join(' ') },
      item.mijn?.gedownload_at && { tekst: `door jou ${downloadTekst(item.mijn)}`, titel: downloadTitel(item.mijn) },
      verloopt,
    ]
    : [{ ...verzonden, tekst: item.verzonden_at ? `Verstuurd ${tijdTekst(item.verzonden_at)}` : '' }, verloopt];
  const primair = soort === 'voor_mij' ? { bgcolor: KLEUR.accent } : { color: KLEUR.zacht };
  return (
    <Box
      ref={ref}
      data-testid={`bestand-${item.id}`}
      onClick={() => onOpen(item)}
      sx={{
        display: 'flex', gap: 2, alignItems: 'flex-start', py: 2, px: 1.5, cursor: 'pointer',
        borderBottom: `1px solid ${KLEUR.lijn}`, borderRadius: 1,
        bgcolor: gemarkeerd ? KLEUR.accentZacht : 'transparent',
        '&:hover': { bgcolor: gemarkeerd ? KLEUR.accentZacht : '#fafbfc' },
      }}
    >
      <Box sx={{ width: 8, pt: 1, flexShrink: 0 }}>
        {ongelezen && <Box aria-label="Nieuw" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: KLEUR.accent }} />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography component="div" sx={{ fontWeight: ongelezen ? 700 : 600, color: KLEUR.tekst, wordBreak: 'break-word',
          display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          {isMap && <FolderOutlinedIcon aria-label="Map" sx={{ fontSize: 18, color: KLEUR.zacht }} />}
          <span>{item.naam}</span>
          <Typography component="span" sx={{ fontSize: 13, color: KLEUR.zacht, fontWeight: 400 }}>
            {isMap ? `${aantalTekst(item.aantal)} · ${grootteTekst(item.grootte)}` : grootteTekst(item.grootte)}
          </Typography>
        </Typography>
        <Regel delen={meta} testId={`meta-${item.id}`} />
        {isMap && !zipKan(item, inhoud) && (
          <Typography data-testid={`geen-zip-${item.id}`} sx={META}>{ZIP_TE_GROOT} via Inhoud.</Typography>
        )}
        {soort === 'verstuurd' && <Ontvangers ontvangers={item.ontvangers} />}
        {soort === 'verstuurd' && <Externen links={item.extern} bezig={bezig} onIntrek={(x) => onIntrek(item, x)} />}
        {item.notitie && (
          <Typography sx={{ fontSize: 14, color: KLEUR.tekst, mt: 0.75, whiteSpace: 'pre-wrap' }}>{item.notitie}</Typography>
        )}
        {inhoud && (
          <Box onClick={(e) => e.stopPropagation()} sx={{ cursor: 'default' }}>
            <Boom items={inhoud} bezig={bezig} onDownload={(b) => onItemDownload(item, b)} />
          </Box>
        )}
      </Box>
      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}
        onClick={(e) => e.stopPropagation()}>
        {isMap ? (zipKan(item, inhoud) && (
          <Button size="small" variant={soort === 'voor_mij' ? 'contained' : 'text'} disableElevation disabled={bezig}
            onClick={() => onZip(item)} sx={{ ...KNOP, ...primair }}>
            Alles als zip
          </Button>
        )) : (
          <Button size="small" variant={soort === 'voor_mij' ? 'contained' : 'text'} disableElevation disabled={bezig}
            onClick={() => onDownload(item)} sx={{ ...KNOP, ...primair }}>
            Download
          </Button>
        )}
        {isMap && (
          <Button size="small" disabled={laden} onClick={wisselInhoud} aria-expanded={Boolean(inhoud)}
            sx={{ ...KNOP, color: KLEUR.zacht }}>
            {inhoud ? 'Inhoud verbergen' : 'Inhoud'}
          </Button>
        )}
        <Button size="small" disabled={bezig} onClick={() => onBewaar(item)} sx={{ ...KNOP, color: KLEUR.zacht }}>
          Bewaren
        </Button>
        {item.mag_verwijderen && (
          <Button size="small" disabled={bezig} onClick={() => onVerwijder(item)} sx={{ ...KNOP, color: KLEUR.zacht }}>
            Verwijderen
          </Button>
        )}
      </Stack>
    </Box>
  );
}

// ── delen ─────────────────────────────────────────────────────────────────
function Overgeslagen({ lijst }) {
  const [alles, setAlles] = useState(false);
  if (!lijst?.length) return null;
  const zichtbaar = alles ? lijst : lijst.slice(0, 5);
  return (
    <Box data-testid="overgeslagen" sx={{ textAlign: 'left', mt: 1.5 }}>
      <Typography sx={{ fontSize: 13, color: KLEUR.zacht, fontWeight: 600 }}>
        Overgeslagen ({lijst.length})
      </Typography>
      {zichtbaar.map((o) => (
        <Typography key={`${o.pad}-${o.reden}`} sx={{ fontSize: 12, color: KLEUR.zacht, wordBreak: 'break-all' }}>
          {o.pad} — {o.reden}
        </Typography>
      ))}
      {lijst.length > 5 && (
        <Button size="small" onClick={() => setAlles((x) => !x)} sx={{ ...KNOP, color: KLEUR.zacht, p: 0, minWidth: 0 }}>
          {alles ? 'Minder' : `En nog ${lijst.length - 5}`}
        </Button>
      )}
    </Box>
  );
}

// Map kiezen: React geeft onbekende kleine-letter-attributen gewoon door aan de DOM.
const MAP_INVOER = { webkitdirectory: '', directory: '' };

export function voortgangTekst(v) {
  if (!v) return '';
  if (v.totaalBestanden && v.bestanden >= v.totaalBestanden) return 'Geüpload';
  const wat = v.totaalBestanden > 1 ? `${Math.min(v.bestanden + 1, v.totaalBestanden)} van ${v.totaalBestanden} bestanden · ` : '';
  const procent = v.procent ?? (v.totaalBytes ? Math.floor((v.bytes / v.totaalBytes) * 100) : 0);
  const rest = v.hapert ? 'verbinding hapert, opnieuw proberen…' : restTekst(v.rest);
  return `Uploaden… ${wat}${grootteTekst(v.bytes)} van ${grootteTekst(v.totaalBytes)} · ${procent}%${rest ? ` · ${rest}` : ''}`;
}

function DeelDialoog({ open, onSluit, onGedeeld }) {
  const [collegas, setCollegas] = useState([]);
  const [keuze, setKeuze] = useState(null);       // { bestanden, overgeslagen, totaal, isMap, naam }
  const [naam, setNaam] = useState('');
  const [aan, setAan] = useState([]);
  const [extern, setExtern] = useState([]);
  const [externTekst, setExternTekst] = useState('');
  const [dagen, setDagen] = useState(String(STANDAARD_DAGEN));
  const [notitie, setNotitie] = useState('');
  const [voortgang, setVoortgang] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [lezen, setLezen] = useState(false);
  const [conceptId, setConceptId] = useState(null);   // geüpload, nog niet verstuurd
  const invoer = useRef(null);
  const mapInvoer = useRef(null);
  const uploadStaat = useRef(nieuweUploadStaat());   // hoort bij de huidige keuze: opnieuw Versturen hervat

  // Tabblad sluiten of verversen tijdens het uploaden/versturen: eerst vragen (de upload stopt dan).
  useEffect(() => {
    if (!bezig) return undefined;
    const waarschuw = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', waarschuw);
    return () => window.removeEventListener('beforeunload', waarschuw);
  }, [bezig]);

  useEffect(() => {
    if (!open) return;
    haalCollegas().then((d) => setCollegas(d.collegas || [])).catch((e) => meldFout(e, "Collega's laden"));
  }, [open]);

  const ruimConceptOp = () => {   // eigen, nog niet verstuurde (of half geüploade) upload: stil opruimen (er is niets gedeeld)
    const id = conceptId || uploadStaat.current.start?.id;
    // ?concept=1: de server verwijdert alleen als het nog niet verstuurd is (ook als het antwoord
    // op versturen verloren ging en de dialoog dus denkt dat het nog een concept is).
    if (id) api(`/${id}?concept=1`, { method: 'DELETE', wat: 'Concept opruimen' }).catch(() => {});
    uploadStaat.current = nieuweUploadStaat();
  };

  const reset = () => {
    setKeuze(null); setNaam(''); setAan([]); setExtern([]); setExternTekst(''); setDagen(String(STANDAARD_DAGEN));
    setNotitie(''); setVoortgang(null); setConceptId(null); setBezig(false);
    uploadStaat.current = nieuweUploadStaat();   // na versturen nooit meer als concept opruimen
  };

  // Elke keuze en elk sluiten krijgt een nieuw volgnummer: een trage, oudere mapleesactie mag
  // een nieuwere keuze (of een gesloten dialoog) nooit meer overschrijven (Codex r1 #7).
  const generatie = useRef(0);

  const sluit = async () => {
    if (bezig) return;
    generatie.current += 1;
    setLezen(false);
    ruimConceptOp();
    reset();
    onSluit();
  };

  const kiesNu = (lijst) => { generatie.current += 1; setLezen(false); kies(lijst); };

  const kies = (lijst, alOvergeslagen = []) => {
    const r = filterBestanden(lijst, alOvergeslagen);
    if (r.fout) { toast.error(r.fout); return; }
    ruimConceptOp();
    setConceptId(null);
    setVoortgang(null);
    setKeuze(r);
    setNaam(r.naam);
  };

  const drop = async (e) => {
    e.preventDefault();
    if (bezig) return;
    const lezer = uitDrop(e.dataTransfer);   // haalt de entries synchroon op, vóór de eerste await
    generatie.current += 1;
    const mijn = generatie.current;
    setLezen(true);
    try {
      const { lijst, overgeslagen } = await lezer;
      if (mijn === generatie.current) kies(lijst, overgeslagen);
    } catch (fout) {
      if (mijn === generatie.current) toast.error(fout?.message || 'Deze map kon niet worden gelezen');
    } finally { if (mijn === generatie.current) setLezen(false); }
  };

  /** Getypte externe adressen (komma, puntkomma, spatie of Enter) toevoegen; ongeldige melden. */
  const voegExternToe = (lijst) => {
    let nieuw = [...extern];
    for (const ruw of lijst) {
      for (const deel of String(ruw || '').split(/[\s,;]+/)) {
        const { adres, fout } = controleerExtern(deel, nieuw);
        if (fout) { toast.error(fout); continue; }
        if (adres && !nieuw.includes(adres)) nieuw = [...nieuw, adres];
      }
    }
    setExtern(nieuw);
    return nieuw;
  };

  const versturen = async () => {
    if (!keuze?.bestanden?.length) { toast.error('Kies een bestand of map'); return; }
    const externen = externTekst.trim() ? voegExternToe([externTekst]) : extern;
    if (externTekst.trim()) setExternTekst('');
    if (!aan.length && !externen.length) { toast.error('Kies minstens één collega of extern adres'); return; }
    const geldig = geldigeDagen(dagen);
    if (externen.length && !geldig) { toast.error(`Geldigheid moet 1 tot ${MAX_DAGEN} dagen zijn`); return; }
    if (externen.length) {
      const ok = await bevestig({
        titel: externen.length === 1 ? 'Naar een extern adres sturen?' : 'Naar externe adressen sturen?',
        tekst: `Je stuurt dit naar ${externen.length === 1 ? 'een extern adres' : 'externe adressen'}: ${externen.join(', ')}. `
          + `${externen.length === 1 ? 'Die krijgt' : 'Elk adres krijgt'} een persoonlijke downloadlink die ${geldig} ${geldig === 1 ? 'dag' : 'dagen'} werkt, zonder inloggen. `
          + 'Je kunt de link later intrekken onder Verstuurd.',
        knop: 'Versturen',
      });
      if (!ok) return;
    }
    setBezig(true);
    try {
      let id = conceptId;
      if (!id) {
        try {
          const r = await uploadPakket({ bestanden: keuze.bestanden, naam: keuze.isMap ? naam.trim() : undefined },
            setVoortgang, uploadStaat.current);
          id = r.id;
        } catch (e) {
          // Wat al binnen is blijft staan (uploadStaat): opnieuw Versturen gaat daar verder.
          if (uploadStaat.current.start) e.message = `${e.message}. Klik nog eens op Versturen om verder te gaan waar de upload was.`;
          throw e;
        }
        setConceptId(id);
      }
      const namen = [...aan.map(voornaam), ...externen].join(', ');
      const r = await voerUitgesteldUit(`Wordt gedeeld met ${namen}…`,
        () => verstuur(id, aan.map((p) => p.email), notitie.trim(), externen, geldig), { duur: 5000 });
      if (r.geannuleerd || r.fout) { setBezig(false); return; }   // upload blijft staan: opnieuw versturen kan meteen
      toast.success(`Gedeeld met ${namen}`);
      reset();
      onGedeeld(r.resultaat);
    } catch (e) {
      meldFout(e, 'Delen');
      setBezig(false);
    }
  };

  const enkel = keuze && !keuze.isMap ? keuze.bestanden[0] : null;
  return (
    <Dialog open={open} onClose={sluit} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>Bestand of map delen</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Box
            data-testid="sleepvlak"
            onDragOver={(e) => e.preventDefault()}
            onDrop={drop}
            sx={{ border: `1px dashed ${KLEUR.lijn}`, borderRadius: 1.5, p: 2.5, textAlign: 'center' }}
          >
            <input ref={invoer} type="file" accept={ACCEPT} multiple hidden data-testid="bestand-invoer"
              onChange={(e) => { kiesNu(uitInvoer(e.target.files)); e.target.value = ''; }} />
            <input ref={mapInvoer} type="file" hidden data-testid="map-invoer" {...MAP_INVOER}
              onChange={(e) => { kiesNu(uitInvoer(e.target.files)); e.target.value = ''; }} />
            {lezen ? (
              <Typography sx={{ color: KLEUR.zacht, fontSize: 14 }}>Map lezen…</Typography>
            ) : enkel ? (
              <Typography sx={{ color: KLEUR.tekst }}>
                {enkel.pad} <Typography component="span" sx={{ color: KLEUR.zacht, fontSize: 13 }}>{grootteTekst(enkel.file.size)}</Typography>
              </Typography>
            ) : keuze ? (
              <Typography component="div" data-testid="gekozen-map"
                sx={{ color: KLEUR.tekst, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <FolderOutlinedIcon sx={{ fontSize: 18, color: KLEUR.zacht }} />
                <span>{naam || keuze.naam}</span>
                <Typography component="span" sx={{ color: KLEUR.zacht, fontSize: 13 }}>
                  {aantalTekst(keuze.bestanden.length)} · {grootteTekst(keuze.totaal)}
                </Typography>
              </Typography>
            ) : (
              <Typography sx={{ color: KLEUR.zacht, fontSize: 14 }}>
                Sleep een bestand of map hierheen ({GRENS_TEKST})
              </Typography>
            )}
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1 }}>
              <Button size="small" disabled={bezig} onClick={() => invoer.current?.click()} sx={{ ...KNOP, color: KLEUR.accent }}>
                {keuze ? 'Andere bestanden' : 'Kies bestanden'}
              </Button>
              <Button size="small" disabled={bezig} onClick={() => mapInvoer.current?.click()} sx={{ ...KNOP, color: KLEUR.accent }}>
                {keuze ? 'Andere map' : 'Kies map'}
              </Button>
            </Stack>
            <Overgeslagen lijst={keuze?.overgeslagen} />
          </Box>
          {keuze?.isMap && (
            <TextField label="Naam van de map" size="small" value={naam} disabled={bezig || Boolean(conceptId)}
              onChange={(e) => setNaam(e.target.value.slice(0, 150))} />
          )}
          <Autocomplete
            multiple options={collegas} value={aan} disabled={bezig}
            onChange={(_, v) => setAan(v)}
            getOptionLabel={(p) => p.naam || p.email}
            isOptionEqualToValue={(a, b) => a.email === b.email}
            renderInput={(params) => <TextField {...params} label="Aan" placeholder="Collega's" size="small" />}
          />
          <Autocomplete
            multiple freeSolo options={[]} value={extern} disabled={bezig}
            inputValue={externTekst}
            onInputChange={(_, v, reden) => {
              if (reden === 'reset') return;
              if (/[\s,;]$/.test(v)) { voegExternToe([v]); setExternTekst(''); } else setExternTekst(v);
            }}
            onChange={(_, v, reden) => {
              if (reden === 'createOption') { voegExternToe([v[v.length - 1]]); setExternTekst(''); } else setExtern(v);
            }}
            onBlur={() => { if (externTekst.trim()) { voegExternToe([externTekst]); setExternTekst(''); } }}
            renderTags={(waarde, tagProps) => waarde.map((adres, i) => (
              <Chip {...tagProps({ index: i })} key={adres} size="small" variant="outlined"
                label={<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>{adres}<ExternLabel /></Box>} />
            ))}
            renderInput={(params) => (
              <TextField {...params} label="Extern" size="small" placeholder={extern.length ? '' : 'naam@bedrijf.nl'}
                helperText={extern.length
                  ? `Krijgt een persoonlijke downloadlink per mail, zonder inloggen (max. ${MAX_EXTERN})`
                  : 'Optioneel: iemand buiten Full Frame, die krijgt een downloadlink per mail'} />
            )}
          />
          {extern.length > 0 && (
            <TextField label="Link geldig (dagen)" size="small" type="number" value={dagen} disabled={bezig}
              inputProps={{ min: 1, max: MAX_DAGEN, step: 1 }} sx={{ maxWidth: 200 }}
              error={!geldigeDagen(dagen)} helperText={geldigeDagen(dagen) ? `1 tot ${MAX_DAGEN} dagen` : `Kies 1 tot ${MAX_DAGEN}`}
              onChange={(e) => setDagen(e.target.value)} />
          )}
          <TextField label="Notitie" size="small" multiline minRows={2} value={notitie} disabled={bezig}
            onChange={(e) => setNotitie(e.target.value.slice(0, 2000))} placeholder="Waar gaat het over? (optioneel)" />
          {voortgang !== null && (
            <Box>
              <LinearProgress variant="determinate" aria-label="Uploadvoortgang"
                value={voortgang.procent ?? (voortgang.totaalBytes ? Math.round((voortgang.bytes / voortgang.totaalBytes) * 100) : 0)}
                sx={{ height: 4, borderRadius: 2, bgcolor: KLEUR.lijn, '& .MuiLinearProgress-bar': { bgcolor: KLEUR.accent } }} />
              <Typography data-testid="voortgang" sx={{ fontSize: 12, color: KLEUR.zacht, mt: 0.5 }}>
                {voortgangTekst(voortgang)}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={sluit} disabled={bezig} sx={{ ...KNOP, color: KLEUR.zacht }}>Annuleren</Button>
        <Button variant="contained" disableElevation onClick={versturen}
          disabled={bezig || lezen || !keuze || (!aan.length && !extern.length && !externTekst.trim())}
          sx={{ ...KNOP, bgcolor: KLEUR.accent }}>
          {bezig ? 'Bezig…' : 'Versturen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Na versturen: externe mails die niet echt de deur uit gingen (test = dry-run, of mislukt). Dan
 *  laten we zien wat er verstuurd zou zijn, met de link om zelf te openen of door te geven. */
export function NietVerstuurd({ regels, onSluit }) {
  const lijst = (regels || []).filter((r) => r.link);
  const kopieer = async (link) => {
    try { await navigator.clipboard.writeText(link); toast.success('Link gekopieerd'); } catch { toast.error('Kopiëren lukte niet'); }
  };
  return (
    <Dialog open={lijst.length > 0} onClose={onSluit} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>Mail naar extern niet verstuurd</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, color: KLEUR.zacht, mb: 2 }}>
          {lijst.every((r) => r.mail === 'dry-run')
            ? 'Op de testomgeving gaat er geen mail naar buiten. Dit zou er verstuurd zijn; de link werkt wel.'
            : 'De mail kon niet worden verstuurd. Je kunt de link zelf doorgeven; hij werkt gewoon.'}
        </Typography>
        {lijst.map((r) => (
          <Box key={r.id} data-testid={`niet-verstuurd-${r.email}`} sx={{ py: 1.25, borderTop: `1px solid ${KLEUR.lijn}` }}>
            <Typography sx={{ fontSize: 14, color: KLEUR.tekst }}>Aan: {r.email}</Typography>
            {r.onderwerp && <Typography sx={{ ...META }}>Onderwerp: {r.onderwerp}</Typography>}
            <Typography sx={{ ...META, wordBreak: 'break-all' }}>{r.link}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Button size="small" onClick={() => kopieer(r.link)} sx={{ ...KNOP, color: KLEUR.accent, p: 0, minWidth: 0 }}>Kopieer link</Button>
              <Button size="small" href={r.link} target="_blank" rel="noopener noreferrer" sx={{ ...KNOP, color: KLEUR.accent, p: 0, minWidth: 0 }}>Open</Button>
            </Stack>
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onSluit} sx={{ ...KNOP, color: KLEUR.accent }}>Sluiten</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Bestanden() {
  const [params, setParams] = useSearchParams();
  const gekozenId = params.get('id');
  const [soort, setSoort] = useState('voor_mij');
  const [items, setItems] = useState(null);
  const [ongelezen, setOngelezen] = useState(0);
  const [bezigId, setBezigId] = useState(null);
  const [dialoog, setDialoog] = useState(false);
  const [nietVerstuurd, setNietVerstuurd] = useState(null);
  // De tab die nu open is en het laatste lijstverzoek: een trage lijst (bv. de verversing na een
  // download) mag nooit de lijst van een andere tab of een nieuwer antwoord overschrijven.
  const huidig = useRef(soort);
  const verzoek = useRef(0);
  const kiesSoort = (v) => { huidig.current = v; setSoort(v); };

  const laad = useCallback(async (welke) => {
    verzoek.current += 1;
    const nr = verzoek.current;
    const actueel = () => nr === verzoek.current && welke === huidig.current;
    try {
      const d = await haalLijst(welke);
      if (!actueel()) return;
      setItems(d.items || []);
      setOngelezen(d.ongelezen || 0);
    } catch (e) {
      if (!actueel()) return;
      meldFout(e, 'Bestanden laden');
      setItems([]);
    }
  }, []);

  // Link uit de mail of de bel: /bestanden?id=… → juiste tab, markeren, (ontvanger) gelezen.
  useEffect(() => {
    if (!gekozenId) { laad(soort); return; }
    let weg = false;
    haalDetail(gekozenId)
      .then((d) => {
        if (weg) return;
        const welke = d.ben_afzender && !(d.ontvangers || []).some((o) => o.email === d.afzender) ? 'verstuurd' : 'voor_mij';
        kiesSoort(welke);
        laad(welke);
      })
      .catch((e) => { if (!weg) { meldFout(e, 'Bestand openen'); laad(soort); } });
    return () => { weg = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gekozenId]);

  const wissel = (_, v) => { kiesSoort(v); setItems(null); laad(v); };
  const open = (item) => { if (item.id !== gekozenId) setParams({ id: item.id }, { replace: true }); };

  const download = async (item) => {
    setBezigId(item.id);
    try {
      const d = await vraagDownload(item.id);
      startDownload(d.url);
      if (huidig.current === 'voor_mij') laad('voor_mij');   // "door jou gedownload …" bijwerken
    } catch (e) { meldFout(e, 'Downloaden'); } finally { setBezigId(null); }
  };

  const zip = async (item) => {
    setBezigId(item.id);
    try {
      const d = await vraagZip(item.id);
      startDownload(zipUrl(d.pad));
      if (huidig.current === 'voor_mij') laad('voor_mij');   // "door jou gedownload …" bijwerken
    } catch (e) { meldFout(e, 'Zip downloaden'); } finally { setBezigId(null); }
  };

  const itemDownload = async (item, bestand) => {
    setBezigId(item.id);
    try {
      const d = await vraagItemDownload(item.id, bestand.id);
      startDownload(d.url);
      if (huidig.current === 'voor_mij') laad('voor_mij');
    } catch (e) { meldFout(e, 'Downloaden'); } finally { setBezigId(null); }
  };

  const bewaren = async (item) => {
    setBezigId(item.id);
    try {
      const d = await bewaar(item.id);
      setItems((lijst) => (lijst || []).map((x) => (x.id === item.id ? { ...x, ...d } : x)));
      toast.success(`Bewaard: ${verlooptTekst(d.verloopt_over_dagen)}`);
    } catch (e) { meldFout(e, 'Bewaren'); } finally { setBezigId(null); }
  };

  const verwijder = async (item) => {
    const namen = (item.ontvangers || []).map(voornaam).join(', ');
    const ok = await bevestig({
      titel: item.is_map ? 'Map verwijderen?' : 'Bestand verwijderen?',
      tekst: `"${item.naam}"${item.is_map ? ` (${aantalTekst(item.aantal)})` : ''} wordt definitief verwijderd${namen ? ` en verdwijnt ook bij ${namen}` : ''}. Dit kan niet ongedaan worden gemaakt.`,
      knop: 'Verwijderen',
      gevaarlijk: true,
    });
    if (!ok) return;
    setBezigId(item.id);
    try {
      await api(`/${encodeURIComponent(item.id)}`, { method: 'DELETE', wat: 'Verwijderen' });
      setItems((lijst) => (lijst || []).filter((x) => x.id !== item.id));
      toast.success('Verwijderd');
    } catch (e) { meldFout(e, 'Verwijderen'); } finally { setBezigId(null); }
  };

  const vervangLink = (item, nieuw) => setItems((lijst) => (lijst || []).map((x) => (x.id !== item.id ? x
    : { ...x, extern: (x.extern || []).map((l) => (l.id === nieuw.id ? { ...l, ...nieuw } : l)) })));

  const intrekken = async (item, link) => {
    setBezigId(item.id);
    try {
      const d = await trekLinkIn(item.id, link.id);
      vervangLink(item, d);
      biedOngedaanAan(`Link voor ${link.email} ingetrokken`, async () => {
        vervangLink(item, await herstelLink(item.id, link.id));
      }, { eigenKnop: true });
    } catch (e) { meldFout(e, 'Intrekken'); } finally { setBezigId(null); }
  };

  const gedeeld = (resultaat) => {
    if ((resultaat?.extern_verstuurd || []).some((r) => r.link)) setNietVerstuurd(resultaat.extern_verstuurd);
    setDialoog(false);
    kiesSoort('verstuurd');
    setItems(null);
    laad('verstuurd');
    if (resultaat?.id) setParams({ id: resultaat.id }, { replace: true });
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader
        icon="send"
        title="Bestanden delen"
        subtitle="Stuur een bestand of een hele map naar een collega of iemand buiten Full Frame. Het blijft 90 dagen staan."
        rightActions={(
          <Button variant="contained" disableElevation onClick={() => setDialoog(true)} sx={{ ...KNOP, bgcolor: KLEUR.accent }}>
            Bestand of map delen
          </Button>
        )}
      />
      <Tabs value={soort} onChange={wissel} sx={{ borderBottom: `1px solid ${KLEUR.lijn}`, mb: 1,
        '& .MuiTab-root': { ...KNOP, minWidth: 0, px: 0, mr: 3 }, '& .MuiTabs-indicator': { bgcolor: KLEUR.accent } }}>
        <Tab value="voor_mij" label={(
          <Stack direction="row" spacing={1} alignItems="center">
            <span>Voor mij</span>
            {ongelezen > 0 && (
              <Box component="span" aria-label={`${ongelezen} ongelezen`} sx={{
                minWidth: 20, height: 20, px: 0.75, borderRadius: 10, bgcolor: KLEUR.accent, color: '#fff',
                fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>{ongelezen}</Box>
            )}
          </Stack>
        )} />
        <Tab value="verstuurd" label="Verstuurd" />
      </Tabs>

      {items === null ? (
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>
      ) : items.length === 0 ? (
        <Typography sx={{ py: 6, textAlign: 'center', color: KLEUR.zacht }}>
          {soort === 'voor_mij' ? 'Er zijn nog geen bestanden met je gedeeld.' : 'Je hebt nog niets gedeeld.'}
        </Typography>
      ) : (
        <Box>
          {items.map((item) => (
            <Rij key={item.id} item={item} soort={soort} gemarkeerd={item.id === gekozenId} bezig={bezigId === item.id}
              onOpen={open} onDownload={download} onZip={zip} onItemDownload={itemDownload}
              onBewaar={bewaren} onVerwijder={verwijder} onIntrek={intrekken} />
          ))}
        </Box>
      )}

      <DeelDialoog open={dialoog} onSluit={() => setDialoog(false)} onGedeeld={gedeeld} />
      <NietVerstuurd regels={nietVerstuurd} onSluit={() => setNietVerstuurd(null)} />
    </Box>
  );
}
